import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { fmtNum, statusClass } from "../lib/format";

function severityTone(s) {
  if (s === "Critical" || s === "High") return "var(--cv-crimson)";
  if (s === "Medium") return "#D55816";
  return "var(--cv-slate)";
}

function riskTone(r) {
  if (r === "Low") return "var(--cv-green)";
  if (r === "Medium") return "#E5C243";
  return "var(--cv-crimson)";
}

export default function AssetManagement() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet("/assetmgmt/summary").then(setD); }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;
  const postureTone = d.posture_score >= 85 ? "var(--cv-green)"
    : d.posture_score >= 70 ? "#D55816" : "var(--cv-crimson)";

  return (
    <>
      <Masthead
        module="assetmgmt"
        eyebrow="IT Asset Management · CMDB · SAM · Lifecycle"
        title="Asset Management"
        subtitle="Hardware &amp; software inventory · EOL/EOSL · warranty · license compliance · encryption &amp; agent posture"
        meta={<div>{fmtNum(k.total_assets)} assets · {fmtNum(k.software_titles)} software titles<br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        {/* Hero */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <button type="button" data-testid="am-posture"
                  onClick={() => nav("/drill/assets")}
                  className="cv-panel text-left hover:shadow-lg transition-shadow"
                  style={{ borderLeft: `4px solid ${postureTone}`, cursor: "pointer" }}>
            <div className="cv-kpi-label">Asset Management Posture</div>
            <div className="flex items-end gap-3 mt-3">
              <div className="font-serif-display leading-none" style={{ color: postureTone, fontSize: 60 }}>
                {d.posture_score}
              </div>
              <div className="pb-2">
                <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>/ 100</div>
                <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>target {d.posture_target}</div>
              </div>
            </div>
            <div className="cv-kpi-sub mt-2">
              Composite of encryption %, lifecycle health, licence compliance &amp; agent coverage.
            </div>
          </button>
          <Kpi testid="am-total" label="Total assets" value={fmtNum(k.total_assets)}
               sub={`${fmtNum(k.workstations)} WS · ${fmtNum(k.servers)} servers · ${fmtNum(k.mobile_iot)} mobile/IoT`}
               onClick={() => nav("/drill/assets")} />
          <Kpi testid="am-encrypted" label="Encrypted" value={`${k.encrypted_pct}%`}
               sub={`${fmtNum(k.unencrypted_count)} unencrypted · policy gap`}
               alert={k.encrypted_pct < 95}
               onClick={() => nav("/drill/assets?encrypted=false")} />
          <Kpi testid="am-unmanaged" label="Unmanaged assets" value={fmtNum(k.unmanaged)}
               sub="discovered but no EDR / MDM agent" alert
               onClick={() => nav("/drill/crowdstrike_devices?status=rfm")} />
        </div>

        {/* Lifecycle & Licenses */}
        <div className="cv-section-label">Lifecycle · Warranty · Licence Compliance</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Kpi testid="am-eol" label="EOL assets" value={fmtNum(k.eol_assets)}
               sub="hardware past end-of-life" alert
               onClick={() => nav("/drill/assets?q=eol")} />
          <Kpi testid="am-eosl" label="EOSL assets" value={fmtNum(k.eosl_assets)}
               sub="past end-of-service-life · vendor won't patch" alert
               onClick={() => nav("/drill/assets?q=eosl")} />
          <Kpi testid="am-warranty" label="Warranty expiring < 90d" value={fmtNum(k.warranty_expiring_90d)}
               sub="renew or replace"
               onClick={() => nav("/drill/assets")} />
          <Kpi testid="am-lic-over" label="Licence over-deployment" value={fmtNum(k.licenses_over)}
               sub={`${fmtNum(k.licenses_under)} under-used · ${fmtNum(k.licenses_expiring_90d)} expiring 90d`}
               alert
               onClick={() => nav("/drill/assets")} />
        </div>

        {/* OS + Lifecycle + Sites */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-os" title="OS Distribution" subtitle="Windows / Linux / macOS mix">
            <div className="flex flex-col gap-2">
              {d.os_distribution.map((o) => {
                const max = Math.max(...d.os_distribution.map(x => x.count));
                const pct = Math.round((o.count / max) * 100);
                const t = o.trend_pct >= 0 ? "var(--cv-green)" : "var(--cv-crimson)";
                return (
                  <button type="button" key={o.os}
                          onClick={() => nav(`/drill/assets?q=${encodeURIComponent(o.os)}`)}
                          className="text-left w-full py-1 hover:opacity-80 transition-opacity"
                          style={{ background: "transparent", border: 0, cursor: "pointer" }}>
                    <div className="flex justify-between items-center text-[13px] mb-0.5">
                      <span className="truncate pr-2" style={{ color: "var(--cv-charcoal)" }}>{o.os}</span>
                      <span className="tabular-nums font-bold" style={{ color: "var(--cv-charcoal)" }}>
                        {fmtNum(o.count)} <span className="text-[11px] font-normal" style={{ color: t }}>({o.trend_pct >= 0 ? "+" : ""}{o.trend_pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--cv-crimson), #D55816)" }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>

          <Panel testid="panel-lifecycle" title="Lifecycle Buckets" subtitle="Where each asset sits on the S-curve">
            <div className="grid grid-cols-2 gap-2">
              {d.lifecycle.map((l) => {
                const tone = l.key === "eosl" || l.key === "eol" ? "var(--cv-crimson)"
                  : l.key === "warn-90" ? "#D55816" : "var(--cv-green)";
                return (
                  <button type="button" key={l.key}
                          data-testid={`lifecycle-${l.key}`}
                          onClick={() => nav("/drill/assets")}
                          className="cv-tile text-left hover:shadow-md transition-shadow"
                          style={{ cursor: "pointer" }}>
                    <div className="cv-tile-label">{l.label}</div>
                    <div className="cv-tile-value" style={{ color: tone }}>{fmtNum(l.count)}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>{l.pct}% of fleet</div>
                  </button>
                );
              })}
            </div>
          </Panel>

          <Panel testid="panel-sites" title="Assets by Site" subtitle="Encryption &amp; unmanaged posture per DC/branch">
            <div className="flex flex-col gap-1.5">
              {d.sites.map((s) => (
                <button type="button" key={s.site}
                        data-testid={`site-${s.site}`}
                        onClick={() => nav(`/drill/assets?q=${encodeURIComponent(s.site)}`)}
                        className="flex justify-between items-center gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{s.site}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{fmtNum(s.assets)} assets · {s.encrypted_pct}% encrypted</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-bold" style={{ color: s.unmanaged > 100 ? "var(--cv-crimson)" : "var(--cv-charcoal)" }}>{s.unmanaged}</div>
                    <div className="text-[10.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>unmanaged</div>
                  </div>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        {/* Software + Licence alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-software" title="Top Software Titles"
                 subtitle={`${fmtNum(k.software_titles)} titles · ${fmtNum(k.software_installs)} installs`}>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">Title</th>
                    <th className="py-2 px-2 font-bold text-right">Installs</th>
                    <th className="py-2 px-2 font-bold text-right">Licensed</th>
                    <th className="py-2 px-2 font-bold text-right">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {d.top_software.map((s) => (
                    <tr key={s.key} data-testid={`sw-${s.key}`}
                        onClick={() => nav("/drill/assets")}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--cv-line)" }}>
                      <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-charcoal)" }}>{s.title}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(s.installs)}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ color: s.licensed && s.installs > s.licensed ? "var(--cv-crimson)" : "var(--cv-charcoal)" }}>{s.licensed ? fmtNum(s.licensed) : "—"}</td>
                      <td className="py-2 px-2 text-right font-bold" style={{ color: riskTone(s.risk) }}>{s.risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testid="panel-licences" title="Licence Compliance Alerts"
                 subtitle="Over-deployed · under-utilised · expiring">
            <div className="flex flex-col gap-1.5">
              {d.license_alerts.map((a) => (
                <button type="button" key={`${a.vendor}|${a.product}|${a.issue}`}
                        onClick={() => nav("/drill/assets")}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{a.vendor} · {a.product}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{a.issue}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-bold" style={{ color: "var(--cv-charcoal)" }}>{a.count}</div>
                    <span className="text-[10.5px] small-caps font-bold" style={{ color: severityTone(a.severity) }}>{a.severity}</span>
                  </div>
                </button>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
